<template>
  <v-form @submit.prevent="onSubmit">
    <v-container fluid>
      <v-row v-if="refForm === 'viewForm'">
        <v-col cols="12">
          <v-alert type="info" dense outlined>
            {{ $t('EmailAlreadySent') }}
          </v-alert>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-text-field
            v-model="item.subject"
            :label="$t('subject')"
            :error-messages="subjectErrors"
            :required="true"
            @blur="$v.item.subject.$touch()"
            @input="$v.item.subject.$touch()"
            outlined
            :disabled="this.refForm === 'viewForm'"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <InputEditor
            v-model="item.body"
            :error-messages="bodyErrors"
            :label="$t('text')"
            :required="true"
            @blur="$v.item.body.$touch()"
            :disabled="this.refForm === 'viewForm'"
            :showSeminarPlaceholders="true"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-checkbox
            v-model="item.sendToNewParticipants"
            :error-messages="sendToNewParticipantsErrors"
            :label="$t('sendToNewParticipants')"
            @blur="$v.item.sendToNewParticipants.$touch()"
            @input="$v.item.sendToNewParticipants.$touch()"
            :disabled="this.refForm === 'viewForm'"
          />
        </v-col>
      </v-row>

      <!-- Attachments -->
      <template v-if="this.refForm !== 'createForm'">
        <v-row>
          <v-col cols="12">
            <p>{{ $t('Attachments') }}:</p>
            <v-list v-if="attachments && attachments.length">
              <v-list-item v-for="att in attachments" :key="att.id">
                <v-list-item-content>
                  <v-list-item-title>
                    <button @click="onDownloadAttachment(att['@id'])">
                      {{ att.name }}
                    </button>
                    <v-icon v-if="refForm !== 'viewForm'" class="ml-4" color="red lighten-1" @click="deleteAttachment(att)">delete</v-icon>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-divider />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6" sm="6">
            <v-file-input
              :rules="fileRules"
              :label="$t('Attachments')"
              v-model="attachment"
              accept="application/pdf,image/*"
              prepend-icon="mdi-attachment"
              outlined
              :disabled="this.refForm === 'viewForm'"
            />
            <v-btn
              class="ml-auto"
              color="primary"
              @click="uploadSeminarEmailAttachment(attachment)"
              :disabled="!attachment || this.refForm === 'viewForm'"
            >
              {{ $t('Upload') }}
            </v-btn>
          </v-col>
        </v-row>
      </template>
      <template v-else>
        <v-row>
          <v-col cols="12" md="6" sm="6">
            <v-alert type="info" dense outlined>
              {{ $t('AttachmentsCanBeAddedAfterSaving') }}
            </v-alert>
          </v-col>
        </v-row>
      </template>
    </v-container>
  </v-form>
</template>

<script>
import has from 'lodash/has';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import InputEditor from '@/components/InputEditor';
import MailAttachmentMixin from '@/mixins/MailAttachmentMixin';
import NotificationMixin from '../../mixins/NotificationMixin';

export default {
  name: 'SeminarEmailForm',
  mixins: [MailAttachmentMixin, validationMixin, NotificationMixin],
  components: { InputEditor },
  props: {
    values: {
      type: Object,
      required: true
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    initialValues: {
      type: Object,
      default: () => ({})
    },
    refForm: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      attachment: null,
      fileRules: [(value) => !value || value.size < 20 * 1024 * 1024 || this.$t('File too large')]
    };
  },
  computed: {
    item() {
      return this.initialValues || this.values;
    },
    attachments() {
      return this.resolveMailAttachments(
        this.refForm === 'createForm' ? (this.item.attachments || []).map((a) => a.iri) : this.item.attachments || []
      );
    },
    subjectErrors() {
      const errors = [];
      if (!this.$v.item.subject.$dirty) return errors;
      has(this.violations, 'subject') && errors.push(this.violations.subject);
      !this.$v.item.subject.required && errors.push(this.$t('Field is required'));
      return errors;
    },
    bodyErrors() {
      const errors = [];
      if (!this.$v.item.body.$dirty) return errors;
      has(this.violations, 'body') && errors.push(this.violations.body);
      !this.$v.item.body.required && errors.push(this.$t('Field is required'));
      return errors;
    },
    sendToNewParticipantsErrors() {
      const errors = [];
      if (!this.$v.item.sendToNewParticipants.$dirty) return errors;
      has(this.violations, 'sendToNewParticipants') && errors.push(this.violations.sendToNewParticipants);

      return errors;
    },
    violations() {
      return this.errors || {};
    }
  },
  validations: {
    item: {
      subject: { required },
      body: { required },
      sendToNewParticipants: {  }
    }
  },
  methods: {
    onSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      this.$emit('submit', {
        subject: this.item.subject,
        body: this.item.body,
        sendToNewParticipants: this.item.sendToNewParticipants
      });
    },
    deleteAttachment(attachment) {
      this.$confirm(this.$t('Anhang wirklich löschen?'), {
        buttonTrueText: this.$t('Yes, delete now'),
        buttonFalseText: this.$t('No')
      }).then((response) => {
        if (!response) {
          return;
        }
        this.deleteMailAttachment({ '@id': attachment['@id'] });

        this.item.attachments = this.item.attachments.filter((att) => {
          return att.id !== attachment['@id'] && att['@id'] !== attachment['@id'];
        });
        this.showMessage(this.$t('AttachmentDeleted'));
      });
    },
    onDownloadAttachment(attachment) {
      this.getAndDownloadMailAttachment(attachment);
    },
    async uploadSeminarEmailAttachment(attachment) {
      this.uploadMailAttachment(attachment, this.item['@id']);
      this.showMessage(this.$t('AttachmentCreated'));
    }
  },
  watch: {
    uploadedMailAttachment() {
      if (!this.uploadedMailAttachment) {
        return;
      }
      if (!this.item.attachments.some((att) => att.id === this.uploadedMailAttachment['@id'])) {
        this.item.attachments.push({
          id: this.uploadedMailAttachment['@id'],
          name: this.uploadedMailAttachment['name'],
          filePath: this.uploadedMailAttachment['filePath']
        });
      }
    }
  }
};
</script>
